"use client";

import { ListingTypeTabs } from "@/components/Sitemap/ListingTypeTabs";
import { P } from "@/components/ui/Heading";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { Emirate } from "@/enums/common.enum";
import { PropertyListingType } from "@/enums/property.enum";
import { Sitemap } from "@/interfaces/sitemap.interface";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import { ContentBlock } from "../Common/ContentBlock";
import { PopularSearchLinkContainer } from "./Links";

export const PopularSearchSection = ({
  data,
}: {
  data: Record<string, Record<string, Sitemap[]>>;
}) => {
  const t = useTranslations("home.popular-searches");
  const st = useTranslations("shared");

  const [selectedPropertyListing, setSelectedPropertyListing] = useState(
    PropertyListingType.RENT
  );
  const [selectedEmirate, setSelectedEmirate] = useState(Emirate.Dubai);

  const getData = useCallback(
    (id: string) => {
      if (data) {
        const filteredData: Record<string, Sitemap[]> = {};

        Object.entries(data[id] ?? {})
          .filter(([property]) => {
            switch (selectedPropertyListing) {
              case PropertyListingType.RENT:
                return ["villa", "townhouse", "apartment"].includes(property);
              case PropertyListingType.BUY:
                return ["villa", "townhouse", "apartment"].includes(property);
              case PropertyListingType.COMMERCIAL_BUY:
                return !["villa", "townhouse", "apartment"].includes(property);
              case PropertyListingType.COMMERCIAL_RENT:
                return !["villa", "townhouse", "apartment"].includes(property);
              default:
                return true;
            }
          })
          .filter(([key]) => key !== "null")
          .map(([key, value]) => {
            filteredData[key] = value;
          });

        return filteredData;
      }
    },
    [data, selectedPropertyListing]
  );

  return (
    <ContentBlock title={t("title")} className="!mb-0" titleElement={"h2"}>
      <div className="space-y-3 sm:space-y-10">
        <div className="space-y-3 sm:space-y-5">
          <ListingTypeTabs
            onChange={(value) => {
              setSelectedPropertyListing(value as PropertyListingType);
              setSelectedEmirate(Emirate.Dubai);
            }}
            value={selectedPropertyListing}
          />

          <Tabs
            variant="underline"
            value={selectedEmirate}
            onValueChange={(value) => setSelectedEmirate(value as Emirate)}
          >
            <div className="overflow-x-auto">
              <TabsList>
                {Object.entries(Emirate)
                  .filter(([, value]) => {
                    const data = Object.keys(getData(value) ?? {});
                    return data?.length || 0 > 0;
                  })
                  .map(([key, value]) => (
                    <TabsTrigger
                      value={value}
                      key={value}
                      className="px-4 capitalize"
                    >
                      {st(
                        `emirates.${key
                          .toLocaleLowerCase()
                          .replaceAll(" ", "-")}`
                      )}
                    </TabsTrigger>
                  ))}
              </TabsList>
            </div>
          </Tabs>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {Object.entries(getData(selectedEmirate) ?? {})
            .filter(([propertyType]) => propertyType !== "null")
            .map(([propertyType, sitemaps]) => (
              <div className="space-y-4" key={propertyType}>
                <P className="text-lg capitalize font-semibold">
                  {st(`property-type.${propertyType}`, { count: 0 })}
                </P>

                <PopularSearchLinkContainer
                  sitemaps={sitemaps as Sitemap[]}
                  truncate={false}
                />
              </div>
            ))}
        </div>
      </div>
    </ContentBlock>
  );
};
